import { AssetEntity } from '../../util/enums';
import { jsUcFirst } from '../Global/StringFormatterFunctions';
import { Property } from '../ProjectConfig/types';
import { AssetTableColumns } from './enums';

export const EMISSIONS_PROPERTY_KEYS = new Set(['emissions_rate', 'emissions_unit']);

export const PRODUCTION_CAPACITY_PROPERTY_KEYS = new Set([
  'production_capacity',
  'production_capacity_unit',
]);

// Keys that have dedicated fixed fields and are therefore excluded from generic property rows.
export const DEDICATED_PROPERTY_KEYS = new Set([
  ...EMISSIONS_PROPERTY_KEYS,
  ...PRODUCTION_CAPACITY_PROPERTY_KEYS,
]);

export const filterEmissionsProperties = (properties: Property[]): Property[] =>
  properties.filter(p => !EMISSIONS_PROPERTY_KEYS.has(p.label));

export const mapDisplayPropertiesToAssetProperties = (
  displayProperties: { index: number; property: string; value: string }[],
): Record<string, string> => {
  const properties: Record<string, string> = {};
  displayProperties.forEach(property => {
    if (!property.property) return;
    if (property.property === 'emissions_rate' && property.value === '') {
      return;
    }
    properties[property.property] = property.value;
  });

  if (!properties.emissions_rate) {
    delete properties.emissions_unit;
  }

  return properties;
};

export const changeColumnTitleForAssetType = (
  column: { id: string; value: string; checked: boolean; index: number },
  type: AssetEntity,
) => {
  if (type === AssetEntity.Device) {
    // Yes, very confusing, here is the rationale...
    // A devices ID (from the backend) is the CODE. A devices LABEL is the ID (as shown to users).
    // But in this case, I need to the LABELs (devices IDs) in the place of where the ID would usually go.
    // And put the LABEL (device CODE) where the LABEL would normally go. This way, everything aligns on the table the way it should without having to change too much of the generic components.
    if (column.id === AssetTableColumns.Label) {
      return {
        ...column,
        value: 'Code',
      };
    } else if (column.id === AssetTableColumns.ID) {
      return {
        ...column,
        value: 'ID',
      };
    }
  }
  if (type === AssetEntity.GpsTracker) {
    if (column.id === AssetTableColumns.ID) {
      return {
        ...column,
        value: 'Serial',
      };
    }
  }
  if (type === AssetEntity.Tool) {
    if (column.id === AssetTableColumns.Label) {
      return {
        ...column,
        value: 'Label/Serial',
      };
    }
  }
  return {
    ...column,
    // Capitalise first letter if not already. This will prevent ID from becoming Id.
    value: /^\p{Lu}/u.test(column.value) ? column.value : jsUcFirst(column.value),
  };
};

export const formContainsNonEmptyRequiredValue = (formState: Record<string, unknown>) => {
  const formValues = Object.entries(formState)
    .map(([key, value]) => ({ key, value }))
    .filter(
      stateValue =>
        stateValue.key === 'id' ||
        stateValue.key === 'label' ||
        stateValue.key === 'tool' ||
        stateValue.key === 'isArchived',
    );
  return !!formValues.find(formValue => formValue.value !== null && formValue.value !== '');
};

export const formContainsEmptyValue = (formState: Record<string, unknown>) => {
  const formValues = Object.entries(formState)
    .map(([key, value]) => ({ key, value }))
    .filter(
      stateValue =>
        stateValue.key === 'id' ||
        stateValue.key === 'label' ||
        stateValue.key === 'tool' ||
        stateValue.key === 'isArchived',
    );
  return !!formValues.find(formValue => formValue.value === null || formValue.value === '');
};
